import * as React from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import Header from '../Header/Header';
import UserContext from '../../context/UserContext/UserContext';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import CustomErrorBoundary from '../CustomErrorBoundary/CustomErrorBoundary';
import NoChatbotEmptyState from '../NoChatbotEmptyState/NoChatbotEmptyState';

const CHATBOT_SCOPED_PREFIXES = [
  '/analytics',
  '/chats',
  '/training',
  '/settings',
  '/embed-instructions'
];

/**
 * Main page content wrapper.
 * Provides the main layout with sidebar, header, and content area.
 */

interface PageWrapperProps {
  children: React.ReactElement;
}

export default function PageWrapper({
  children
}: Readonly<PageWrapperProps>): React.ReactElement {
  const { user } = React.useContext(UserContext);
  const { currentChatbot, initialized } = React.useContext(ChatbotContext);
  const location = useLocation();

  const routeRequiresChatbot = CHATBOT_SCOPED_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );
  const showNoChatbotState =
    routeRequiresChatbot && initialized && !currentChatbot;

  const isBotBuilderPage = (): boolean => {
    if (location.pathname.startsWith('/bot-builder')) {
      if (
        !location.pathname.endsWith('build') &&
        !location.pathname.startsWith('/bot-builder/questions')
      ) {
        return true;
      }
    }
    return false;
  };

  const isAuthPage = !user?.mfaComplete;

  const getMainClasses = (): string => {
    const classes = 'overflow-y-auto min-w-0';

    if (isAuthPage) {
      return classes;
    }

    if (isBotBuilderPage()) {
      return `${classes} p-4`;
    }

    return `${classes} pt-3 md:pt-6 pb-4 md:pb-8 pl-4 pr-3 md:pl-8 md:pr-6`;
  };

  return (
    <div className='flex h-screen bg-background overflow-x-hidden'>
      {user?.mfaComplete && !isBotBuilderPage() && <Navbar />}
      <div className='flex flex-col flex-1 min-w-0'>
        {!isBotBuilderPage() && user?.mfaComplete && <Header />}
        <main
          className={getMainClasses()}
          style={isAuthPage ? undefined : { scrollbarGutter: 'stable' }}
        >
          <CustomErrorBoundary>
            {showNoChatbotState ? <NoChatbotEmptyState /> : children}
          </CustomErrorBoundary>
        </main>
      </div>
    </div>
  );
}
